import React from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  Switch,
  Slider,
  LinearProgress,
  CircularProgress,
  Alert,
  Divider,
  Grid,
  Container,
  IconButton,
  Fab,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Rating,
  Skeleton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Pagination,
  Breadcrumbs,
  Link,
  Badge,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Drawer,
  AppBar,
  Toolbar,
  Tooltip,
  Menu,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  OutlinedInput,
  FormHelperText,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from '@mui/lab';
import {
  Person,
  Email,
  Phone,
  LocationOn,
  Star,
  Favorite,
  Share,
  MoreVert,
  Home,
  NavigateNext,
  ExpandMore,
  Check,
  Settings,
  Print,
  Save,
  Delete,
  Edit,
  Add,
  Search,
  Notifications,
  AccountCircle,
  Dashboard,
  Assessment,
  Business,
  School,
  Work,
  Event,
  LocalOffer,
  TrendingUp,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';

export default function ThemePreview() {
  const [value, setValue] = React.useState(2);
  const [sliderValue, setSliderValue] = React.useState(30);
  const [activeStep, setActiveStep] = React.useState(0);
  const [expanded, setExpanded] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleSliderChange = (event, newValue) => {
    setSliderValue(newValue);
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const steps = [
    {
      label: 'Select campaign settings',
      description: `For each ad campaign that you create, you can control how much
                you're willing to spend on clicks and conversions, which networks
                and geographical locations you want your ads to show on, and more.`,
    },
    {
      label: 'Create an ad group',
      description:
        'An ad group contains one or more ads which target a shared set of keywords.',
    },
    {
      label: 'Create an ad',
      description: `Try out different ad text to see what brings in the most customers,
                and learn how to enhance your ads using features like ad extensions.
                If you run into any problems with your ads, find out how to tell if
                they're running and how to resolve approval issues.`,
    },
  ];

  const actions = [
    { icon: <Edit />, name: 'Copy' },
    { icon: <Save />, name: 'Save' },
    { icon: <Print />, name: 'Print' },
    { icon: <Share />, name: 'Share' },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="display1" gutterBottom align="center" sx={{ mb: 6, color: 'primary.main' }}>
        ✨ Enhanced Professional Bright Theme ✨
      </Typography>

      <Typography variant="h5" gutterBottom align="center" sx={{ mb: 4, color: 'text.secondary' }}>
        Experience the next level of design sophistication with enhanced colors, animations, and visual effects
      </Typography>

      {/* Enhanced Color Palette Showcase */}
      <Card sx={{ mb: 6, background: 'linear-gradient(135deg, #F8FAFC 0%, #FFFFFF 100%)' }}>
        <CardHeader 
          title="Enhanced Color Palette" 
          subheader="Professional color system with gradients and soft variants"
          titleTypographyProps={{ variant: 'h4', fontWeight: 700 }}
        />
        <CardContent>
          <Grid container spacing={3}>
            {/* Primary Colors */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mb: 2 }}>
                Primary Colors
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: 3,
                      background: 'primary.gradient',
                      mx: 'auto',
                      mb: 1,
                      boxShadow: 'customShadows.primary',
                    }}
                  />
                  <Typography variant="caption" fontWeight={600}>Primary</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: 3,
                      background: 'primary.soft',
                      mx: 'auto',
                      mb: 1,
                      border: '2px solid',
                      borderColor: 'primary.light',
                    }}
                  />
                  <Typography variant="caption" fontWeight={600}>Soft</Typography>
                </Box>
              </Box>
            </Grid>

            {/* Accent Colors */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom sx={{ color: 'secondary.main', mb: 2 }}>
                Accent Colors
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {['success', 'warning', 'error', 'info'].map((color) => (
                  <Box key={color} sx={{ textAlign: 'center' }}>
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: 2,
                        bgcolor: `${color}.main`,
                        mx: 'auto',
                        mb: 1,
                        boxShadow: `customShadows.${color}`,
                      }}
                    />
                    <Typography variant="caption" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                      {color}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Enhanced Interactive Components */}
      <Card sx={{ mb: 6 }}>
        <CardHeader 
          title="Enhanced Interactive Elements" 
          subheader="Sophisticated animations and hover effects"
          titleTypographyProps={{ variant: 'h4', fontWeight: 700 }}
        />
        <CardContent>
          <Grid container spacing={4}>
            {/* Enhanced Buttons */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                Enhanced Buttons
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button 
                  variant="contained" 
                  color="primary"
                  startIcon={<Add />}
                  sx={{ 
                    background: 'primary.gradient',
                    boxShadow: 'customShadows.primary',
                    '&:hover': {
                      boxShadow: 'customShadows.glow',
                    }
                  }}
                >
                  Primary
                </Button>
                <Button 
                  variant="contained" 
                  color="secondary"
                  startIcon={<Edit />}
                  sx={{ 
                    background: 'secondary.gradient',
                    boxShadow: 'customShadows.secondary',
                  }}
                >
                  Secondary
                </Button>
                <Button 
                  variant="outlined" 
                  color="success"
                  startIcon={<Check />}
                  sx={{ 
                    borderWidth: 2,
                    '&:hover': {
                      borderWidth: 3,
                      transform: 'translateY(-2px)',
                    }
                  }}
                >
                  Success
                </Button>
              </Box>
            </Grid>

            {/* Enhanced Form Elements */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                Enhanced Form Elements
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Enhanced Input"
                  variant="outlined"
                  placeholder="Experience the enhanced styling"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover': {
                        transform: 'translateY(-1px)',
                      }
                    }
                  }}
                />
                <FormControl fullWidth>
                  <InputLabel>Enhanced Select</InputLabel>
                  <Select
                    value=""
                    label="Enhanced Select"
                    startAdornment={<Search />}
                  >
                    <MenuItem value="option1">Option 1</MenuItem>
                    <MenuItem value="option2">Option 2</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Enhanced Data Display */}
      <Card sx={{ mb: 6 }}>
        <CardHeader 
          title="Enhanced Data Display" 
          subheader="Sophisticated tables, cards, and data visualization"
          titleTypographyProps={{ variant: 'h4', fontWeight: 700 }}
        />
        <CardContent>
          <Grid container spacing={4}>
            {/* Enhanced Table */}
            <Grid item xs={12} lg={8}>
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                Enhanced Data Table
              </Typography>
              <TableContainer component={Paper} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'background.subtle' }}>
                      <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>User</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Role</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[
                      { name: 'John Doe', role: 'Admin', status: 'Active', avatar: 'J' },
                      { name: 'Jane Smith', role: 'User', status: 'Pending', avatar: 'J' },
                      { name: 'Bob Johnson', role: 'Moderator', status: 'Active', avatar: 'B' },
                    ].map((user, index) => (
                      <TableRow key={index} sx={{ '&:hover': { bgcolor: 'surface.primary' } }}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: 'primary.main' }}>{user.avatar}</Avatar>
                            <Typography variant="body2" fontWeight={500}>{user.name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={user.role} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={user.status} 
                            size="small" 
                            color={user.status === 'Active' ? 'success' : 'warning'}
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton size="small" color="primary">
                              <Edit />
                            </IconButton>
                            <IconButton size="small" color="secondary">
                              <Visibility />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            {/* Enhanced Cards */}
            <Grid item xs={12} lg={4}>
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                Enhanced Cards
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Card className="modern-card" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'success.main' }}>
                      <TrendingUp />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight={600}>Revenue Growth</Typography>
                      <Typography variant="body2" color="text.secondary">+15.3% this month</Typography>
                    </Box>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={75} 
                    sx={{ 
                      height: 8, 
                      borderRadius: 4,
                      bgcolor: 'grey.200',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 4,
                        background: 'success.gradient',
                      }
                    }} 
                  />
                </Card>

                <Card className="modern-card" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'info.main' }}>
                      <Business />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight={600}>Active Users</Typography>
                      <Typography variant="body2" color="text.secondary">2,847 online</Typography>
                    </Box>
                  </Box>
                  <CircularProgress 
                    variant="determinate" 
                    value={68} 
                    size={60}
                    sx={{ 
                      color: 'info.main',
                      filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))',
                    }} 
                  />
                </Card>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Enhanced Navigation & Layout */}
      <Card sx={{ mb: 6 }}>
        <CardHeader 
          title="Enhanced Navigation & Layout" 
          subheader="Sophisticated navigation patterns and layout components"
          titleTypographyProps={{ variant: 'h4', fontWeight: 700 }}
        />
        <CardContent>
          <Grid container spacing={4}>
            {/* Enhanced App Bar */}
            <Grid item xs={12}>
              <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
                <AppBar position="static" elevation={0} sx={{ bgcolor: 'background.paper' }}>
                  <Toolbar>
                    <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600, flexGrow: 1 }}>
                      Enhanced App Bar
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton color="primary">
                        <Search />
                      </IconButton>
                      <IconButton color="primary">
                        <Notifications />
                      </IconButton>
                      <IconButton color="primary">
                        <AccountCircle />
                      </IconButton>
                    </Box>
                  </Toolbar>
                </AppBar>
              </Paper>
            </Grid>

            {/* Enhanced Navigation */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                Enhanced Navigation
              </Typography>
              <Paper sx={{ borderRadius: 3, p: 2 }}>
                <List>
                  {[
                    { icon: <Dashboard />, text: 'Dashboard', active: true },
                    { icon: <Assessment />, text: 'Analytics' },
                    { icon: <Business />, text: 'Business' },
                    { icon: <School />, text: 'Education' },
                  ].map((item, index) => (
                    <ListItem 
                      key={index}
                      disablePadding 
                      sx={{ mb: 1 }}
                      className={item.active ? 'Mui-selected' : ''}
                    >
                      <ListItemButton
                        sx={{
                          borderRadius: 2,
                          '&:hover': {
                            bgcolor: 'surface.primary',
                          }
                        }}
                      >
                        <ListItemIcon sx={{ color: item.active ? 'primary.main' : 'text.secondary' }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText 
                          primary={item.text} 
                          primaryTypographyProps={{ 
                            fontWeight: item.active ? 600 : 500,
                            color: item.active ? 'primary.main' : 'text.primary'
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>

            {/* Enhanced Breadcrumbs */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                Enhanced Breadcrumbs
              </Typography>
              <Paper sx={{ borderRadius: 3, p: 3 }}>
                <Breadcrumbs 
                  separator={<NavigateNext fontSize="small" />}
                  aria-label="breadcrumb"
                >
                  <Link color="inherit" href="#" underline="hover">
                    <Home sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                    Home
                  </Link>
                  <Link color="inherit" href="#" underline="hover">
                    Dashboard
                  </Link>
                  <Typography color="text.primary" fontWeight={600}>
                    Analytics
                  </Typography>
                </Breadcrumbs>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Enhanced Interactive Components */}
      <Card sx={{ mb: 6 }}>
        <CardHeader 
          title="Advanced Interactive Components" 
          subheader="Sophisticated form controls and interactive elements"
          titleTypographyProps={{ variant: 'h4', fontWeight: 700 }}
        />
        <CardContent>
          <Grid container spacing={4}>
            {/* Enhanced Form Controls */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                Enhanced Form Controls
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box>
                  <Typography variant="body2" gutterBottom>Enhanced Switch</Typography>
                  <Switch defaultChecked color="primary" />
                </Box>
                <Box>
                  <Typography variant="body2" gutterBottom>Enhanced Slider</Typography>
                  <Slider
                    value={sliderValue}
                    onChange={handleSliderChange}
                    aria-label="Enhanced Slider"
                    color="primary"
                    sx={{
                      '& .MuiSlider-thumb': {
                        boxShadow: 'customShadows.z8',
                      }
                    }}
                  />
                </Box>
                <Box>
                  <Typography variant="body2" gutterBottom>Enhanced Rating</Typography>
                  <Rating
                    value={value}
                    onChange={handleChange}
                    precision={0.5}
                    size="large"
                    sx={{
                      '& .MuiRating-iconFilled': {
                        filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))',
                      }
                    }}
                  />
                </Box>
              </Box>
            </Grid>

            {/* Enhanced Progress & Loading */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                Enhanced Progress & Loading
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box>
                  <Typography variant="body2" gutterBottom>Linear Progress</Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={75} 
                    sx={{ 
                      height: 8, 
                      borderRadius: 4,
                      bgcolor: 'grey.200',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 4,
                        background: 'primary.gradient',
                      }
                    }} 
                  />
                </Box>
                <Box>
                  <Typography variant="body2" gutterBottom>Circular Progress</Typography>
                  <CircularProgress 
                    variant="determinate" 
                    value={68} 
                    size={60}
                    sx={{ 
                      color: 'primary.main',
                      filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))',
                    }} 
                  />
                </Box>
                <Box>
                  <Typography variant="body2" gutterBottom>Skeleton Loading</Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Skeleton variant="circular" width={40} height={40} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Skeleton variant="text" width="60%" />
                      <Skeleton variant="text" width="40%" />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Enhanced Data Visualization */}
      <Card sx={{ mb: 6 }}>
        <CardHeader 
          title="Enhanced Data Visualization" 
          subheader="Sophisticated charts, timelines, and data presentation"
          titleTypographyProps={{ variant: 'h4', fontWeight: 700 }}
        />
        <CardContent>
          <Grid container spacing={4}>
            {/* Enhanced Timeline */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                Enhanced Timeline
              </Typography>
              <Timeline position="alternate">
                {[
                  { icon: <Event />, title: 'Project Started', description: 'Initial planning phase' },
                  { icon: <Work />, title: 'Development', description: 'Core development work' },
                  { icon: <Check />, title: 'Testing', description: 'Quality assurance phase' },
                  { icon: <Star />, title: 'Launch', description: 'Product release' },
                ].map((item, index) => (
                  <TimelineItem key={index}>
                    <TimelineSeparator>
                      <TimelineDot 
                        color={index === 3 ? 'success' : 'primary'}
                        sx={{ 
                          boxShadow: 'customShadows.z8',
                          '&:hover': {
                            transform: 'scale(1.1)',
                          }
                        }}
                      >
                        {item.icon}
                      </TimelineDot>
                      {index < 3 && (
                        <TimelineConnector sx={{ bgcolor: 'primary.main' }} />
                      )}
                    </TimelineSeparator>
                    <TimelineContent>
                      <Paper elevation={3} sx={{ p: 2, borderRadius: 2 }}>
                        <Typography variant="h6" component="span" fontWeight={600}>
                          {item.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {item.description}
                        </Typography>
                      </Paper>
                    </TimelineContent>
                  </TimelineItem>
                ))}
              </Timeline>
            </Grid>

            {/* Enhanced Stepper */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                Enhanced Stepper
              </Typography>
              <Paper sx={{ p: 3, borderRadius: 3 }}>
                <Stepper activeStep={activeStep} orientation="vertical">
                  {steps.map((step, index) => (
                    <Step key={step.label}>
                      <StepLabel
                        StepIconProps={{
                          sx: {
                            color: index <= activeStep ? 'primary.main' : 'grey.400',
                            '&.Mui-completed': {
                              color: 'success.main',
                            }
                          }
                        }}
                      >
                        {step.label}
                      </StepLabel>
                      <StepContent>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {step.description}
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                          <Button
                            variant="contained"
                            onClick={handleNext}
                            sx={{ mr: 1 }}
                          >
                            {index === steps.length - 1 ? 'Finish' : 'Continue'}
                          </Button>
                          <Button
                            disabled={index === 0}
                            onClick={handleBack}
                            sx={{ mr: 1 }}
                          >
                            Back
                          </Button>
                        </Box>
                      </StepContent>
                    </Step>
                  ))}
                </Stepper>
                {activeStep === steps.length && (
                  <Paper square elevation={0} sx={{ p: 3, mt: 3, bgcolor: 'success.soft', borderRadius: 2 }}>
                    <Typography variant="h6" color="success.main" gutterBottom>
                      All steps completed - you&apos;re finished
                    </Typography>
                    <Button onClick={handleReset} sx={{ mt: 1, mr: 1 }}>
                      Reset
                    </Button>
                  </Paper>
                )}
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Enhanced Alerts & Notifications */}
      <Card sx={{ mb: 6 }}>
        <CardHeader 
          title="Enhanced Alerts & Notifications" 
          subheader="Sophisticated feedback and notification systems"
          titleTypographyProps={{ variant: 'h4', fontWeight: 700 }}
        />
        <CardContent>
          <Grid container spacing={3}>
            {[
              { severity: 'success', title: 'Success Alert', message: 'Operation completed successfully with enhanced styling' },
              { severity: 'info', title: 'Info Alert', message: 'This is an informational message with enhanced design' },
              { severity: 'warning', title: 'Warning Alert', message: 'Please review the following warning information' },
              { severity: 'error', title: 'Error Alert', message: 'An error occurred, please check the details' },
            ].map((alert, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Alert 
                  severity={alert.severity} 
                  variant="filled"
                  sx={{ 
                    borderRadius: 2,
                    '& .MuiAlert-icon': {
                      fontSize: '1.5rem',
                    },
                    '& .MuiAlert-message': {
                      fontWeight: 500,
                    }
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    {alert.title}
                  </Typography>
                  {alert.message}
                </Alert>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Enhanced Floating Actions */}
      <Card sx={{ mb: 6 }}>
        <CardHeader 
          title="Enhanced Floating Actions" 
          subheader="Sophisticated floating action buttons and speed dials"
          titleTypographyProps={{ variant: 'h4', fontWeight: 700 }}
        />
        <CardContent>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                Enhanced FAB
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Fab 
                  color="primary" 
                  aria-label="add"
                  sx={{ 
                    background: 'primary.gradient',
                    boxShadow: 'customShadows.float',
                    '&:hover': {
                      boxShadow: 'customShadows.floatHover',
                    }
                  }}
                >
                  <Add />
                </Fab>
                <Fab 
                  color="secondary" 
                  aria-label="edit"
                  sx={{ 
                    background: 'secondary.gradient',
                    boxShadow: 'customShadows.float',
                  }}
                >
                  <Edit />
                </Fab>
                <Fab 
                  color="success" 
                  aria-label="save"
                  sx={{ 
                    background: 'success.gradient',
                    boxShadow: 'customShadows.float',
                  }}
                >
                  <Save />
                </Fab>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                Enhanced Speed Dial
              </Typography>
              <Box sx={{ position: 'relative', height: 200 }}>
                <SpeedDial
                  ariaLabel="Enhanced Speed Dial"
                  sx={{
                    position: 'absolute',
                    bottom: 16,
                    right: 16,
                    '& .MuiFab-primary': {
                      background: 'primary.gradient',
                      boxShadow: 'customShadows.float',
                      '&:hover': {
                        background: 'primary.dark',
                        boxShadow: 'customShadows.floatHover',
                      }
                    }
                  }}
                  icon={<SpeedDialIcon />}
                >
                  {actions.map((action) => (
                    <SpeedDialAction
                      key={action.name}
                      icon={action.icon}
                      tooltipTitle={action.name}
                      sx={{
                        '& .MuiFab-secondary': {
                          background: 'white',
                          color: 'text.primary',
                          boxShadow: 'customShadows.z8',
                          '&:hover': {
                            background: 'primary.soft',
                            color: 'primary.main',
                          }
                        }
                      }}
                    />
                  ))}
                </SpeedDial>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Enhanced Pagination & Navigation */}
      <Card sx={{ mb: 6 }}>
        <CardHeader 
          title="Enhanced Pagination & Navigation" 
          subheader="Sophisticated pagination and navigation patterns"
          titleTypographyProps={{ variant: 'h4', fontWeight: 700 }}
        />
        <CardContent>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                Enhanced Pagination
              </Typography>
              <Pagination 
                count={10} 
                variant="outlined" 
                shape="rounded"
                color="primary"
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: 2,
                    '&.Mui-selected': {
                      background: 'primary.gradient',
                      color: 'white',
                      boxShadow: 'customShadows.primary',
                    }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                Enhanced Accordion
              </Typography>
              <Accordion 
                expanded={expanded === 'panel1'} 
                onChange={handleAccordionChange('panel1')}
                sx={{ 
                  borderRadius: 2,
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': {
                    boxShadow: 'customShadows.z8',
                  }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{ fontWeight: 600 }}
                >
                  <Typography variant="subtitle1" fontWeight={600}>
                    Enhanced Accordion Section
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary">
                    This accordion demonstrates enhanced styling with sophisticated shadows,
                    smooth transitions, and improved visual hierarchy.
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Footer */}
      <Box sx={{ 
        textAlign: 'center', 
        mt: 8, 
        p: 4, 
        background: 'linear-gradient(135deg, #F8FAFC 0%, #E2E8F0 100%)', 
        borderRadius: 4,
        border: '1px solid',
        borderColor: 'border.light'
      }}>
        <Typography variant="h4" color="primary.main" gutterBottom fontWeight={700}>
          🎉 Enhanced Professional Theme Complete! 🎉
        </Typography>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Your application now features a sophisticated, professional design system
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
          Experience enhanced colors, sophisticated animations, improved accessibility, 
          and professional visual hierarchy that elevates your user experience to the next level.
        </Typography>
      </Box>
    </Container>
  );
} 